import { Injectable } from '@angular/core';
import { PermissionCode, Tenant } from '@app/shared/enums';
import { CustomerCenter } from '@app/shared/services/api/customer-center.service';
import { EditableMunicipalityConfig, Municipality } from '@app/shared/services/api/municipality.service';
import { Permission } from '@app/shared/services/api/permission.service';
import { StateService } from '@app/shared/services/state/state.service';
import { StringUtility } from '@app/shared/string-utility';
import { environment } from '@env/environment';
import { isEqual } from 'lodash';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';
import { CustomerCenterConfig, MunicipalityStoreConfig, RegionAssignment } from '../models/municipality-management';

export const initialMunicipalityConfigState: MunicipalityStoreConfig = {
  municipality: {} as Municipality,
  customerCenter: {} as CustomerCenterConfig,
  pagePermissions: [],
  regionAssignment: {} as RegionAssignment,
  isLoading: false,
  canNavigate: true,
};

@Injectable({
  providedIn: 'root',
})
export class MunicipalityManagementConfigurationStore extends StateService<MunicipalityStoreConfig> {
  private _oldRegionId: string[] = [];
  private _isFormValid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private _configStache: MunicipalityStoreConfig = {} as MunicipalityStoreConfig;
  private _hasNoOrphanedMunicipality?: boolean;
  private _updatedExterneUrl = false;

  constructor() {
    super(initialMunicipalityConfigState);
  }

  public get canNavigate(): boolean {
    return this.state.canNavigate;
  }

  public get updatedExterneUrl(): boolean {
    return this._updatedExterneUrl;
  }

  public set updatedExterneUrl(value: boolean) {
    this._updatedExterneUrl = value;
  }

  public municipalityStoreConfiguration$: Observable<MunicipalityStoreConfig> = this.select((config: MunicipalityStoreConfig) => config).pipe(shareReplay(1));

  public get municipalityStoreConfig(): MunicipalityStoreConfig {
    return this.state;
  }

  public municipalityConfig$: Observable<EditableMunicipalityConfig> = this.select((config: MunicipalityStoreConfig) => config.municipality).pipe(
    shareReplay(1)
  );

  public isMunicipalityActive$: Observable<boolean> = this.municipalityConfig$.pipe(switchMap((municipalityConfig) => of(!municipalityConfig.isDeactivated)));

  public get municipalityConfig(): EditableMunicipalityConfig {
    return this.state.municipality;
  }

  public municipalityRegionAssignment$: Observable<RegionAssignment> = this.select((config: MunicipalityStoreConfig) => config.regionAssignment).pipe(
    shareReplay(1)
  );

  public get municipalityRegionAssignment(): RegionAssignment {
    return this.state.regionAssignment;
  }

  public municipalityCustomerCenterConfig$: Observable<CustomerCenterConfig> = this.select((config: MunicipalityStoreConfig) => config.customerCenter).pipe(
    shareReplay(1)
  );

  public get municipalityCustomerCenterConfig(): CustomerCenterConfig {
    return this.state.customerCenter;
  }

  public municipalityBasicFeaturesConfig$: Observable<Permission[]> = this.select((config: MunicipalityStoreConfig) => {
    const basicFeatures = config.pagePermissions.filter((permission) => !permission.isPremium);

    basicFeatures.sort((a, b) => {
      return a.name.localeCompare(b.name, 'de');
    });

    return basicFeatures;
  }).pipe(shareReplay(1));

  public municipalityPremiumFeaturesConfig$: Observable<Permission[]> = this.select((config: MunicipalityStoreConfig) => {
    let premiumFeatures = config.pagePermissions.filter((permission) => permission.isPremium);

    premiumFeatures = premiumFeatures.map((permission) => {
      permission.allowLinkUpdate = true;
      switch (permission.code) {
        case PermissionCode.LOCAL_ELECTRICITY_MARKET_LINK:
          permission.link = this.state.municipality.localElectricityMarketUrl;
          break;
        case PermissionCode.CO2_BALANCE_SHEET_LINK:
          permission.link = this.state.municipality.climateNaviUrl;
          break;
        case PermissionCode.ENERGY_MONITOR:
          permission.link = this.state.municipality.energyMonitorUrl;
          break;
        case PermissionCode.ROOM_MONITORING:
          permission.link = this.state.municipality.roomMonitoringUrl;
          break;
        default:
          permission.allowLinkUpdate = false;
          break;
      }

      if (
        !StringUtility.hasAnyMatchInArray([environment.tenant], [Tenant.BAYERNWERK, Tenant.SHNETZ]) &&
        permission.code === PermissionCode.ENERGIE_MANAGEMENT_OPTIMUM
      ) {
        permission.isTeaserDisabled = true;
      }

      return permission;
    });

    premiumFeatures.sort((a, b) => {
      return a.name.localeCompare(b.name, 'de');
    });
    return premiumFeatures;
  }).pipe(shareReplay(1));

  public get municipalityFeaturesConfig(): Permission[] {
    return this.state.pagePermissions;
  }

  public clear(): void {
    this.setState(initialMunicipalityConfigState);
  }

  public initializeConfigState = (config: MunicipalityStoreConfig): void => {
    Object.assign(this._configStache, config);
    this.setState({
      ...config,
      canNavigate: true,
    });
  };

  public setMunicipalityConfigState = (config: MunicipalityStoreConfig): void => {
    this.setState({
      ...config,
      canNavigate: true,
    });
  };

  public setMunicipalityConfig = (config: Partial<EditableMunicipalityConfig>): void => {
    this.setState({
      ...this.state,
      municipality: { ...this.state.municipality, ...config },
      canNavigate: false,
    });
  };

  public setRegionAssignmentConfig = (config: Partial<RegionAssignment>): void => {
    this.setState({
      ...this.state,
      regionAssignment: { ...this.state.regionAssignment, ...config },
      canNavigate: false,
    });
  };

  public setFeaturesConfig = (permissions: Permission[]): void => {
    this.setState({
      ...this.state,
      pagePermissions: permissions,
      canNavigate: false,
    });
  };

  public setCustomerCenterConfig = (config: CustomerCenter): void => {
    this.setState({
      ...this.state,
      customerCenter: {
        ...this.state.customerCenter,
        selected: config.name,
        damageReportEmailAddress: config.damageReportEmailAddress,
      },
      canNavigate: false,
    });
  };

  public setCanNavigateValue = (config: boolean = false): void => {
    this.setState({
      ...this.state,
      canNavigate: config,
    });
  };

  public resetChangesToStore = (): void => {
    this._isFormValid.next(true);
    return this.setState(this._configStache);
  };

  public get oldRegionId() {
    return this._oldRegionId;
  }

  public set oldRegionId(id: string[]) {
    this._oldRegionId = id;
  }

  public municipalityHasChanges(): boolean {
    return !isEqual(this._configStache.municipality, this.state.municipality);
  }

  public featuresHasChanges(): boolean {
    const configStachePagePermissions = this._configStache.pagePermissions;
    const statePagePermissions = this.state.pagePermissions;
    const featuresHasChanges = !isEqual(configStachePagePermissions, statePagePermissions);
    return featuresHasChanges;
  }

  public regionHasChanges(): boolean {
    return !isEqual(this._configStache.regionAssignment?.regionId, this.state.regionAssignment?.regionId);
  }

  public setFormValidity(isValid: boolean) {
    this._isFormValid.next(isValid);
  }

  public isFormValid$: Observable<boolean> = this._isFormValid;

  public get isFormValid(): boolean {
    return this._isFormValid.value;
  }

  public get hasNoOrphanedMunicipality(): boolean | undefined {
    return this._hasNoOrphanedMunicipality;
  }

  public set hasNoOrphanedMunicipality(value: boolean | undefined) {
    this._hasNoOrphanedMunicipality = value;
  }
}
